import { inject, Injectable } from '@angular/core';
import { RoutingEnum } from '@shared/enums/routing.enum';
import { environment } from '@environment/environment';
import { BehaviorSubject, combineLatest, distinctUntilChanged, exhaustMap, Observable, of, retry, startWith, switchMap, timer } from 'rxjs';
import { User } from '@capacitor-firebase/authentication';
import { FirebaseAuthService } from '@services/utility-services/firebase-auth.service';
import { Router } from '@angular/router';
import { AppUpdateService } from '@services/app-update.service';
import { TermsAndConditionsService } from '@services/terms-and-conditions.service';
import { BackgroundService } from '@services/utility-services/background.service';
import { HealthService } from '@services/api/health.service';
import { NavController } from '@ionic/angular/common';
import { AuthService } from '@services/auth-state-services/auth.service';
import { isStellaAccessRevoked, PersonService, StellaPersonLoadState } from '@services/stella-state-services/person.service';
import { StartupTraceUtil } from '@shared/utils/startup-trace.util';
import { RegistrationService } from '@bpp-stella/common/state/auth/registration.service';
import { StartupWarmupService } from '@services/utility-services/startup-warmup.service';

@Injectable({
  providedIn: 'root'
})
export class LoadingService {
  private readonly firebaseAuthService = inject(FirebaseAuthService);
  private readonly router = inject(Router);
  private readonly appUpdateService = inject(AppUpdateService);
  private readonly termsAndConditionsService = inject(TermsAndConditionsService);
  private readonly backgroundService = inject(BackgroundService);
  private readonly authService = inject(AuthService);
  private readonly healthService = inject(HealthService);
  private readonly navController = inject(NavController);
  private readonly registrationService = inject(RegistrationService);
  private readonly personService = inject(PersonService);
  private readonly startupWarmupService = inject(StartupWarmupService);

  private static readonly ROUTING_RETRY_BASE_DELAY_MS = 1000;
  private static readonly ROUTING_RETRY_MAX_DELAY_MS = 30_000;

  public readonly updateCheckTrigger$ = new BehaviorSubject<void>(undefined);

  /** Last decision the pipeline navigated on (undefined until the first one). */
  private lastDecision: RoutingEnum | undefined;
  /** One-shot override for the next LOGIN navigation — see {@link requestLoginTarget}. */
  private pendingLoginTarget: string | null = null;
  /** Set by a NO_CONNECTION decision: the next HOME reloads the per-person data that failed meanwhile. */
  private isRecoveringFromNoConnection: boolean = false;

  public handleRouting(): void {
    // Called at app start, starting app with loading-screen and routingPipeline will then route to correct target screen
    void this.router.navigate(['/loading'], {
      replaceUrl: true,
      skipLocationChange: true
    });

    this.routingPipeline()
      .pipe(
        // This is the app's only routing subscription: an error from any gate member used to tear
        // it down for good, so nothing re-navigated until the app was restarted. The retry is
        // deliberately unbounded — giving up would reintroduce that dead end, and every screen the
        // app could strand the user on recovers by pushing into this very pipeline. Backing off to
        // ROUTING_RETRY_MAX_DELAY_MS keeps a permanently unreachable backend down to two requests a
        // minute. distinctUntilChanged sits downstream and keeps its last value, so a recovered
        // pipeline that reaches the same conclusion does not re-navigate.
        retry({ delay: (error: unknown, attempt: number) => this.scheduleRoutingRetry(error, attempt) }),
        distinctUntilChanged()
      )
      .subscribe({
        next: routingDestinations => {
          StartupTraceUtil.mark('routing-decided', RoutingEnum[routingDestinations]);
          this.lastDecision = routingDestinations;
          // The index.html launch splash, for a first decision that beats the loading screen
          // (LoadingScreenComponent drops it otherwise); a no-op once it is gone.
          document.getElementById('launch-splash')?.remove();
          switch (routingDestinations) {
            case RoutingEnum.HOME:
              if (this.isRecoveringFromNoConnection) {
                this.isRecoveringFromNoConnection = false;
                this.startupWarmupService.reloadPersonData();
              }
              void this.navController.navigateRoot(this.backgroundService.previousUrl() ?? environment.appEntryPage);
              break;
            case RoutingEnum.INITIAL_AUTH:
              void this.router.navigate(['/initialAuth'], {
                skipLocationChange: true
              });
              break;
            case RoutingEnum.PERMISSION_REVOKED:
              void this.router.navigate(['/permission-revoked'], {
                skipLocationChange: true
              });
              break;
            case RoutingEnum.TERMS_AND_CONDITIONS:
              void this.router.navigate(['/terms-and-conditions'], {
                skipLocationChange: true
              });
              break;
            case RoutingEnum.UPDATE:
              void this.router.navigate(['/update'], {
                skipLocationChange: true
              });
              break;
            case RoutingEnum.LOADING:
              void this.router.navigate(['/loading'], {
                skipLocationChange: true
              });
              break;
            case RoutingEnum.NO_CONNECTION:
              this.isRecoveringFromNoConnection = true;
              void this.router.navigate(['/error'], {
                replaceUrl: true,
                skipLocationChange: true
              });
              break;
            case RoutingEnum.REGISTRATION:
              // Deliberately no navigation: the registration flow is mid-step
              // and navigating would abandon it.
              break;
            case RoutingEnum.LOGIN:
            default:
              this.navigateToLogin();
          }
        }
      });
  }

  /**
   * Makes the next LOGIN decision land on `target` instead of /welcome — used by the
   * password-reset link (AppComponent.handleAppUrlOpen). Navigating around the pipeline does not work: it re-emits LOGIN
   * as soon as a sign-out publishes null (or on cold start once the gate resolves), strictly after
   * any navigation issued directly, and always won. If the pipeline has already settled on LOGIN
   * there is no re-emission to consume the target, so it is applied right away.
   */
  public requestLoginTarget(target: string): void {
    this.pendingLoginTarget = target;

    if (this.lastDecision === RoutingEnum.LOGIN) {
      this.navigateToLogin();
    }
  }

  private navigateToLogin(): void {
    void this.navController.navigateRoot(this.pendingLoginTarget ?? '/welcome');
    this.pendingLoginTarget = null;
  }

  private scheduleRoutingRetry(error: unknown, attempt: number): Observable<number> {
    console.error(`Routing gate failed (attempt ${ attempt }), retrying:`, error);

    return timer(Math.min(
      LoadingService.ROUTING_RETRY_BASE_DELAY_MS * 2 ** (attempt - 1),
      LoadingService.ROUTING_RETRY_MAX_DELAY_MS
    ));
  }

  private routingPipeline(): Observable<RoutingEnum> {
    const observables = {
      // Re-probed on every updateCheckTrigger$ push, not once per pipeline subscription. getHealth
      // is cold, completing, and swallows per-backend failures, so it can never error — which means
      // the retry below can never re-run it and a DOWN result would otherwise pin the app on /error
      // for the life of the process, however quickly the backend came back.
      // exhaustMap, not switchMap: a push while a probe is still running is answered by that probe.
      // switchMap cancelled it instead, so on a link slower than the retry interval no probe ever
      // finished and /error never cleared.
      apiHealth: this.updateCheckTrigger$.pipe(
        exhaustMap(() => this.healthService.getHealth())
      ),
      user: this.firebaseAuthService.currentUser$,
      isUpdateAvailable: this.updateCheckTrigger$.pipe(
        exhaustMap(() => this.appUpdateService.checkIfUpdateIsAvailable())
      ),
      personLoaded: this.authService.personLoaded$,
      /*
       * The auth-side login only knows the identity; whether the person may still use Stella is
       * bpp-stella's answer, and it gives it on this lookup — 401 once StellaAccess is gone from
       * every customer. PersonService makes the request anyway; the gate just listens to it.
       */
      stellaPersonLoaded: this.personService.personLoaded$,
      // Starts the moment the auth login resolves, in parallel with the rest of the gate —
      // previously this ran only after every other gate member had completed, adding a full
      // sequential round trip to every startup.
      hasAcceptedTerms: this.authService.personLoaded$.pipe(
        switchMap(({ isLoaded, error }): Observable<boolean | null> => {
          if (!isLoaded || error !== null) {
            return of(null);
          }

          // startWith(null): a fresh person evaluation must not reuse the previous answer
          return this.termsAndConditionsService.checkIfUserHasTermsAndConditionsAccepted().pipe(
            startWith(null)
          );
        })
      )
    };

    return combineLatest(observables).pipe(
      switchMap(({
                   apiHealth,
                   user,
                   isUpdateAvailable,
                   personLoaded,
                   stellaPersonLoaded,
                   hasAcceptedTerms
                 }): Observable<RoutingEnum> => {
        if (this.backgroundService.isOffline() || apiHealth.overall !== 'Healthy') {
          return of(RoutingEnum.NO_CONNECTION);
        }

        return this.checkUpdateAndUser(user, isUpdateAvailable, personLoaded, stellaPersonLoaded, hasAcceptedTerms);
      })
    );
  }

  private checkUpdateAndUser(
    user: User | null,
    isUpdateAvailable: boolean,
    personLoaded: { isLoaded: boolean, error: number | null },
    stellaPersonLoaded: StellaPersonLoadState,
    hasAcceptedTerms: boolean | null
  ): Observable<RoutingEnum> {
    if (isUpdateAvailable) {
      return of(RoutingEnum.UPDATE);
    }

    if (!user) {
      return of(RoutingEnum.LOGIN);
    }

    if (!personLoaded.isLoaded) {
      return of(RoutingEnum.LOADING);
    }

    if (personLoaded.isLoaded && (personLoaded.error === 403 || personLoaded.error === 404)) {
      // A registration in progress produces exactly this state between creating
      // the Firebase account and redeeming the invitation code. Those screens
      // handle it themselves, so sending the user to the linking screen here
      // would tear the flow apart one step before it completes.
      return of(this.registrationService.isFlowActive() ? RoutingEnum.REGISTRATION : RoutingEnum.INITIAL_AUTH);
    }

    if (personLoaded.isLoaded && personLoaded.error === 401) {
      return of(RoutingEnum.PERMISSION_REVOKED);
    }

    if (personLoaded.isLoaded && personLoaded.error !== null) {
      return of(RoutingEnum.LOGIN);
    }

    if (this.registrationService.isFlowActive()) {
      // The auth login fired on account creation races activate: when activate wins, the person
      // resolves while the user is still on the done screen. Its "start" ends the flow and reloads
      // the auth login, which is what moves on to the terms or home.
      return of(RoutingEnum.REGISTRATION);
    }

    // After the auth-side branches: registration's 403/404 must win before Stella is consulted.
    if (!stellaPersonLoaded.isLoaded) {
      return of(RoutingEnum.LOADING);
    }

    if (isStellaAccessRevoked(stellaPersonLoaded)) {
      return of(RoutingEnum.PERMISSION_REVOKED);
    }

    if (stellaPersonLoaded.error !== null) {
      // An answer the app could not obtain holds the gate; the loading screen retries the lookup.
      return of(RoutingEnum.LOADING);
    }

    if (hasAcceptedTerms === null) {
      // Person is loaded but the terms answer for them is still in flight — stay on loading
      return of(RoutingEnum.LOADING);
    }

    return of(hasAcceptedTerms ? RoutingEnum.HOME : RoutingEnum.TERMS_AND_CONDITIONS);
  }
}
